/* @flow */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom'
import { MARKDOWN_FILE_ACTIONS, MARKDOWN_CURRENT_FILE } from 'src/constants/actionTypes';

import CmEditor from 'src/components/slides-editor/CmEditor';
import {
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Checkbox,
  Toolbar,
  Box,
  Divider,
  Button,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  UploadFile as UploadFileIcon,
  FileDownload as DownloadIcon,
  DragIndicator as DragIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Public as PublishIcon,
  Close as CloseIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import MarkdownPreview from './MarkdownPreview';
import { Trash } from '@styled-icons/bootstrap';
import { useIntl } from 'react-intl';
import { AI_DRAFTER, FLOW_MODAL, OPERATION_FAILED, SETTINGS_DIALOG } from '../../constants/actionTypes';
import { callAIAction, logout_of_server, uploadFiles } from '../../actions/ticketAction';
import { LogoutBoxR } from '@styled-icons/remix-line';
import { Settings } from '@styled-icons/remix-line/Settings';

import { toPng } from 'html-to-image';
import ImageIcon from '@mui/icons-material/Image';
import { ArrowMaximize } from '@styled-icons/fluentui-system-filled';
import { isMac } from '../../constants/constants';
import { getMainDomain, isElectronApp, openExternalLink } from '../../utils/constants';
import { Slideshow } from '@styled-icons/boxicons-regular';
import { MindMap } from '@styled-icons/remix-editor';
import logo from '../../logo.png';

const MdEditorFull = ({ style, embedded }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const history = useHistory();
  const location = useLocation();
  const params = new Proxy(new URLSearchParams(location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });

  // Redux state
  const markdownFilesById = useSelector(state => state.markdownFiles.byId || {});
  const markdownFileIds = useSelector(state => state.markdownFiles.allIds || []);
  const currentFileId = useSelector(state => state.uiState.currentMarkdownFileId);
  const loginUser = useSelector(state => state.loginIn.user);

  // Memoize markdownFiles array
  const memoizedMarkdownFiles = useMemo(() => {
    return markdownFileIds.map(id => markdownFilesById[id]).filter(Boolean);
  }, [markdownFileIds, markdownFilesById]);

  // Local state
  const [leftWidth, setLeftWidth] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [scrollSync, setScrollSync] = useState(true);
  const [isPreviewEnabled, setIsPreviewEnabled] = useState(true);
  const [content, setContent] = useState('');
  const [focusOn, setFocusOn] = useState();
  const [loading, setLoading] = useState();
  const [hoveredTabId, setHoveredTabId] = useState(null);

  const OSName = isMac ? 'mac' : 'win';

  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);

  const [fileMenuAnchor, setFileMenuAnchor] = useState(null);
  const fileMenuOpen = Boolean(fileMenuAnchor);

  // WordPress dialog state
  const [wpDialogOpen, setWpDialogOpen] = useState(false);
  const [wpConfig, setWpConfig] = useState({
    siteUrl: '',
    username: '',
    password: '',
    title: ''
  });

  const editorRef = useRef(null);
  const previewRef = useRef(null);
  const fileInputRef = useRef(null);

  // Initialize default file if none exists
  useEffect(() => {
    if (markdownFileIds.length === 0) {
      const newFileId = `file_${Date.now()}`;
      const defaultFile = {
        id: newFileId,
        name: 'Untitled',
        content: '# Untitled',
        createdAt: new Date().getTime(),
        updatedAt: new Date().getTime(),
        filePath: null,
        isSaved: false,
        isLocalFile: false
      };
      dispatch({
        type: MARKDOWN_FILE_ACTIONS.received,
        item: defaultFile
      });
      dispatch({
        type: MARKDOWN_CURRENT_FILE,
        value: newFileId
      });
    } else {
      if (!currentFileId) {
        dispatch({
          type: MARKDOWN_CURRENT_FILE,
          value: markdownFileIds[0]
        });
      }
    }
  }, [dispatch, markdownFileIds.length]);

  useEffect(() => {
    const initContent = params.initContent;

    if (initContent) {
      try {
        // Decode the content (in case it's URL encoded)
        // const decodedContent = decodeURIComponent(initContent);

        // Create a new file with the init content
        const newFileId = `file_${Date.now()}`;

        // Extract first heading as filename if exists
        const lines = initContent.trim().split('\n');
        const firstNonEmptyLine = lines.find(line => line.trim() !== '');
        let fileName = 'Untitled.md';

        if (firstNonEmptyLine) {
          const headingPattern = /^#+\s+/;
          const isHeading = headingPattern.test(firstNonEmptyLine.trim());

          if (isHeading) {
            const headingText = extractHeadingTextCompat(firstNonEmptyLine);
            if (headingText) {
              fileName = `${headingText}.md`;
            }
          }
        }

        const newFile = {
          id: newFileId,
          name: fileName,
          content: initContent,
          createdAt: new Date().getTime(),
          updatedAt: new Date().getTime(),
          filePath: null,
          isSaved: false,
          isLocalFile: false
        };

        // Dispatch actions to create and set current file
        dispatch({
          type: MARKDOWN_FILE_ACTIONS.received,
          item: newFile
        });
        dispatch({
          type: MARKDOWN_CURRENT_FILE,
          value: newFileId
        });

        // Optional: Clean up URL to remove initContent parameter
        // This prevents re-creating the file on refresh
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('initContent');
        const newSearch = searchParams.toString();
        const newPath = `${location.pathname}?${newSearch}`;

        history.replace(newPath);

      } catch (error) {
        console.error('Failed to process initContent:', error);
        // Optionally dispatch an error message
        dispatch({
          type: OPERATION_FAILED,
          message: 'Failed to load initial content'
        });
      }
    }
  }, [params.initContent, dispatch]);

  // Get current file
  const currentFile = useMemo(() => {
    return currentFileId && markdownFilesById[currentFileId] || memoizedMarkdownFiles[0];
  }, [markdownFilesById, currentFileId, memoizedMarkdownFiles]);

  // Handle export to local file
  const handleExportLocal = useCallback(() => {
    if (!currentFile) return;

    const blob = new Blob([currentFile.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = currentFile.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExportMenuAnchor(null);
  }, [currentFile]);

  // Initialize content from current file
  useEffect(() => {
    if (currentFile) {
      setContent(currentFile.content || '');
    }
  }, [currentFile?.id]);

  // Handle file selection
  const handleFileChange = useCallback((fileId) => {
    dispatch({
      type: MARKDOWN_CURRENT_FILE,
      value: fileId
    });
  }, [dispatch]);

  // Handle new file creation
  const handleNewFile = useCallback(() => {
    const newFile = {
      id: `file_${Date.now()}`,
      name: `Untitled_${memoizedMarkdownFiles.length + 1}`,
      content: '# Untitled',
      createdAt: new Date().getTime(),
      updatedAt: new Date().getTime(),
      filePath: null,
      isSaved: false,
      isLocalFile: false
    };

    dispatch({
      type: MARKDOWN_FILE_ACTIONS.received,
      item: newFile
    });
    dispatch({
      type: MARKDOWN_CURRENT_FILE,
      value: newFile.id
    });

    // dispatch({
    //   type: AI_DRAFTER,
    //   value: {
    //     visible: true
    //   }
    // })
  }, [dispatch, memoizedMarkdownFiles.length]);

  // Handle close file (for tab close button)
  const handleCloseFile = useCallback((event, file) => {
    event.stopPropagation(); // Prevent tab selection when clicking close button

    const closeFile = () => {
      dispatch({
        type: MARKDOWN_FILE_ACTIONS.invalidated,
        item: file
      });
      if (window.electronAPI) {
        window.electronAPI.fileClosed(file);
      }
      // Switch to another file if this was the current file
      if (file.id === currentFileId) {
        const remainingFiles = memoizedMarkdownFiles.filter(f => f.id !== file.id);
        dispatch({
          type: MARKDOWN_CURRENT_FILE,
          value: remainingFiles[0]?.id
        });
      }
    };

    if (window.electronAPI) {
      const hasUnsavedChanges = file.content !== file.originalContent;
      if (hasUnsavedChanges) {
        dispatch({
          type: 'CONFIRM_DIALOG',
          value: {
            visible: true,
            handleConfirm: closeFile,
            content: 'This file has unsaved changes. Are you sure you want to close it?'
          }
        });
      } else {
        closeFile();
      }
    } else {
      // For non-electron environment, always show confirmation
      dispatch({
        type: 'CONFIRM_DIALOG',
        value: {
          visible: true,
          handleConfirm: closeFile,
          content: 'Are you sure you want to close this file?'
        }
      });
    }
  }, [dispatch, currentFileId, memoizedMarkdownFiles]);

  const openConfirmDialog = (handleConfirm) => dispatch({
    type: 'CONFIRM_DIALOG', value: {
      visible: true,
      handleConfirm,
      content: intl.formatMessage({ id: 'confirm_delete_markdown' })
    }
  });

  // Handle import file
  const handleImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      // Keep file name without extension for imported files (not saved locally yet)
      let displayName = file.name;
      if (displayName.endsWith('.txt') || displayName.endsWith('.text') || displayName.endsWith('.md') || displayName.endsWith('.markdown')) {
        displayName = displayName.replace(/\.(txt|text|md|markdown)$/, '');
      }

      const newFile = {
        id: `file_${Date.now()}`,
        name: displayName,
        content: event.target.result,
        createdAt: new Date().getTime(),
        updatedAt: new Date().getTime(),
        filePath: null,
        isSaved: false,
        isLocalFile: false
      };

      dispatch({
        type: MARKDOWN_FILE_ACTIONS.received,
        item: newFile
      });
      dispatch({
        type: MARKDOWN_CURRENT_FILE,
        value: newFile.id
      });
    };
    reader.readAsText(file);
    e.target.value = '';
  }, [dispatch]);



  const handleExportPDF = useCallback(async () => {
    if (!currentFile) return;

    setExportMenuAnchor(null);
    setLoading(true);

    const element = previewRef.current;
    if (!element) {
      setLoading(false);
      return;
    }

    try {
      const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
        .map(s => s.outerHTML)
        .join('\n');
      const htmlContent = element.innerHTML;
      const baseName = (currentFile.name || 'document').replace(/\.md$/i, '');
      const fileName = `${baseName}.pdf`;

      if (window.electronAPI && window.electronAPI.printToPDF) {
        window.electronAPI.printToPDF({
          html: htmlContent,
          css: styles,
          defaultFileName: fileName
        });
      } else {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(`
          <!DOCTYPE html>
          <html lang="zh-CN">
            <head>
              <meta charset="utf-8">
              <title>${baseName}</title>
              ${styles}
              <style>
                body {
                  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "WenQuanYi Micro Hei";
                }
                @media print {
                  @page { margin: 10mm; }
                  body { background: white !important; margin: 0; padding: 0; }
                }
              </style>
            </head>
            <body>
              <div class="markdown-body" style="padding: 20px;">
                ${htmlContent}
              </div>
              <script>
                setTimeout(() => {
                  window.print();
                }, 500);
              </script>
            </body>
          </html>
        `);
        iframeDoc.close();

        // Temporarily override main document title for browser save-dialog defaults
        const originalTitle = document.title;
        document.title = baseName;

        // Remove iframe after print dialog resolves or timeouts (10s), and restore title
        setTimeout(() => {
          document.title = originalTitle;
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 10000);
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      // Small timeout to let UI settle
      setTimeout(() => setLoading(false), 500);
    }
  }, [currentFile]);


  const handleExportImage = async () => {
    setExportMenuAnchor(null);
    setLoading(true);

    try {
      // 获取预览区域的元素
      const previewElement = previewRef.current;

      if (!previewElement) {
        console.error('Preview element not found');
        return;
      }

      // 保存原始样式
      const originalOverflow = previewElement.style.overflow;
      const originalHeight = previewElement.style.height;
      const originalMaxHeight = previewElement.style.maxHeight;

      // 临时移除滚动，显示完整内容
      previewElement.style.overflow = 'visible';
      previewElement.style.height = 'auto';
      previewElement.style.maxHeight = 'none';

      // 等待布局更新
      await new Promise(resolve => setTimeout(resolve, 100));

      // 使用 html-to-image 生成图片
      const dataUrl = await toPng(previewElement, {
        cacheBust: true,
        pixelRatio: 2, // 提高清晰度
        backgroundColor: '#ffffff',
        width: previewElement.scrollWidth,
        height: previewElement.scrollHeight,
      });

      // 恢复原始样式
      previewElement.style.overflow = originalOverflow;
      previewElement.style.height = originalHeight;
      previewElement.style.maxHeight = originalMaxHeight;

      // 下载图片
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${currentFile?.name.replace('.md', '') || 'document'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting image:', error);

      // 确保即使出错也恢复样式
      if (previewRef.current) {
        previewRef.current.style.overflow = '';
        previewRef.current.style.height = '';
        previewRef.current.style.maxHeight = '';
      }
    } finally {
      setLoading(false);
    }
  };

  const isValidToProcess = useCallback(() => {
    setExportMenuAnchor(null);

    if (!loginUser?._id) {
      dispatch({
        type: FLOW_MODAL,
        value: {
          visible: true,
          action: 'login',
        }
      })
      return false;
    }

    if (!currentFile?.content || currentFile?.content.replaceAll('#', '').trim()?.toLowerCase() === 'untitled') {
      dispatch({ type: OPERATION_FAILED, message: 'No content' });
      return false;
    };

    return true;

  }, [currentFile, loginUser])

  const handleSaveToFunBlocks = useCallback(() => {
    if (!isValidToProcess()) return;

    const lines = currentFile.content.split('\n');
    while (lines.length > 0 && lines[0].trim() === '') {
      lines.shift();
    }

    const firstline = lines[0]?.trim();

    if (firstline.startsWith('#') && firstline.endsWith(currentFile.name?.trim()?.replace('.md', ''))) {
      lines.shift();
    }

    let data = {
      files: [{
        content: lines.join('\n'),
        name: currentFile.name,
        lastModified: currentFile.updatedAt
      }],
      fileType: 'md',
      space: 'private'
    };

    setLoading(true);
    dispatch(uploadFiles({
      data,
      enctype: 'multipart'
    }, (doc) => {
      setLoading(false);

      if (!doc) {
        return dispatch({ type: OPERATION_FAILED, message: 'Failed to import page' });
      }

      dispatch({
        type: FLOW_MODAL,
        value: {
          visible: true,
          action: 'saved_to_workspace',
          data: {
            hid: doc.hid
          }
        }
      })

    }, (msg) => {
      setLoading(false);
      return dispatch({ type: OPERATION_FAILED, message: 'Failed to save to workspace: ' + (msg || 'Unknown error') });
    }, 'editor'));

  }, [isValidToProcess]);

  const handleConvertSlides = useCallback(() => {
    if (!isValidToProcess()) return;

    setLoading(true);
    dispatch(callAIAction({
      data: {
        app: 'slides',
        content: currentFile.content
      }
    }, (doc) => {
      setLoading(false);
      if (!doc) {
        return dispatch({ type: OPERATION_FAILED, message: 'Failed to import page' });
      }

      dispatch({
        type: FLOW_MODAL,
        value: {
          visible: true,
          action: 'saved_to_workspace',
          data: {
            viewer: 'slidesEditor',
            hid: doc.hid
          }
        }
      })
    }, (msg) => {
      setLoading(false);

      return dispatch({ type: OPERATION_FAILED, message: 'Failed to convert to slides: ' + (msg || 'Unknown error') });
    }, 'editor'));

  }, [isValidToProcess]);

  const handleConvertFlow = useCallback(() => {
    if (!isValidToProcess()) return;

    window.open(`/#/aiflow?nodeType=note&action=mindmap&content=${encodeURIComponent(currentFile.content)}`, '_blank');

  }, [isValidToProcess])

  // Handle save to Google Drive
  const handleSaveToGoogleDrive = useCallback(() => {
    if (!currentFile) return;

    // Google Drive API configuration
    const CLIENT_ID = 'YOUR_GOOGLE_CLIENT_ID';
    const API_KEY = 'YOUR_GOOGLE_API_KEY';
    const SCOPES = 'https://www.googleapis.com/auth/drive.file';

    const initGoogleAPI = () => {
      window.gapi.load('client:auth2', async () => {
        try {
          await window.gapi.client.init({
            apiKey: API_KEY,
            clientId: CLIENT_ID,
            discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'],
            scope: SCOPES
          });

          const authInstance = window.gapi.auth2.getAuthInstance();
          if (!authInstance.isSignedIn.get()) {
            await authInstance.signIn();
          }

          uploadToGoogleDrive();
        } catch (error) {
          alert('Google Drive error: ' + error.message);
        }
      });
    };

    const uploadToGoogleDrive = async () => {
      const file = new Blob([currentFile.content], { type: 'text/markdown' });
      const metadata = {
        name: currentFile.name,
        mimeType: 'text/markdown'
      };

      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', file);

      try {
        const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
          method: 'POST',
          headers: new Headers({
            'Authorization': 'Bearer ' + window.gapi.auth.getToken().access_token
          }),
          body: form
        });

        if (response.ok) {
          alert('Successfully saved to Google Drive!');
        } else {
          alert('Failed to save to Google Drive');
        }
      } catch (error) {
        alert('Error: ' + error.message);
      }
    };

    // Load Google API script
    if (!window.gapi) {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = initGoogleAPI;
      document.body.appendChild(script);
    } else {
      initGoogleAPI();
    }

    setExportMenuAnchor(null);
  }, [currentFile]);

  // Handle save to OneDrive
  const handleSaveToOneDrive = useCallback(() => {
    if (!currentFile) return;

    const CLIENT_ID = 'YOUR_ONEDRIVE_CLIENT_ID';
    const REDIRECT_URI = window.location.origin;

    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${CLIENT_ID}&scope=files.readwrite&response_type=token&redirect_uri=${REDIRECT_URI}`;

    const popup = window.open(authUrl, 'OneDrive Auth', 'width=500,height=600');

    const messageHandler = async (event) => {
      if (event.data.type === 'onedrive-token') {
        const token = event.data.token;

        try {
          const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/root:/${currentFile.name}:/content`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'text/markdown'
            },
            body: currentFile.content
          });

          if (response.ok) {
            alert('Successfully saved to OneDrive!');
          } else {
            alert('Failed to save to OneDrive');
          }
        } catch (error) {
          alert('Error: ' + error.message);
        }

        popup.close();
        window.removeEventListener('message', messageHandler);
      }
    };

    window.addEventListener('message', messageHandler);
    setExportMenuAnchor(null);
  }, [currentFile]);

  // Handle publish to WordPress
  const handlePublishToWordPress = useCallback(async () => {
    if (!currentFile) return;

    const { siteUrl, username, password, title } = wpConfig;

    if (!siteUrl || !username || !password) {
      alert('Please fill in all WordPress credentials');
      return;
    }

    try {
      const credentials = btoa(`${username}:${password}`);
      const postTitle = title || currentFile.name.replace('.md', '');

      const response = await fetch(`${siteUrl}/wp-json/wp/v2/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: postTitle,
          content: currentFile.content,
          status: 'draft'
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Successfully published to WordPress!\nPost ID: ${data.id}\nStatus: Draft`);
        setWpDialogOpen(false);
        setWpConfig({ siteUrl: '', username: '', password: '', title: '' });
      } else {
        const error = await response.text();
        alert(`Failed to publish to WordPress:\n${error}`);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  }, [currentFile, wpConfig]);

  function extractHeadingTextCompat(headingLine) {
    if (!headingLine || typeof headingLine !== 'string') return '';

    let text = headingLine.replace(/^#+\s*/, '');
    text = text.replace(/\*\*(.+?)\*\*/g, '$1');
    text = text.replace(/__(.+?)__/g, '$1');
    text = text.replace(/\*(.+?)\*/g, '$1');
    text = text.replace(/_(.+?)_/g, '$1');
    text = text.replace(/~~(.+?)~~/g, '$1');
    text = text.replace(/`(.+?)`/g, '$1');
    text = text.replace(/\[(.+?)\]\(.+?\)/g, '$1');
    text = text.replace(/!\[(.+?)\]\(.+?\)/g, '$1');
    text = text.replace(/<[^>]+>/g, '');
    text = text.trim();
    text = text.replace(/[<>:"/\\|?*]/g, '-');
    text = text.replace(/\s+/g, ' ');
    text = text.replace(/-+/g, '-');

    const maxLength = 100;
    if (text.length > maxLength) {
      text = text.substring(0, maxLength).trim();
    }

    return text;
  }

  // Handle content change
  const handleContentChange = useCallback((content, cursorLine) => {
    setContent(content);

    if (!currentFile || !content?.trim()) return;

    let updatedFile = {
      ...currentFile,
      content,
      updatedAt: new Date().getTime()
    };

    // Only update name based on heading if file is not saved locally or not from local file
    if (!currentFile.isSaved && !currentFile.isLocalFile) {
      const lines = content.trim().split('\n');
      const firstNonEmptyLine = lines.find(line => line.trim() !== '');

      if (firstNonEmptyLine) {
        const headingPattern = /^#+\s+/;
        const isHeading = headingPattern.test(firstNonEmptyLine.trim());

        if (isHeading) {
          const headingText = extractHeadingTextCompat(firstNonEmptyLine);
          if (headingText) {
            updatedFile.name = headingText;
          }
        }
      }
    }

    dispatch({
      type: MARKDOWN_FILE_ACTIONS.updated,
      item: updatedFile
    });
  }, [currentFile, dispatch]);

  // Setup Electron IPC listeners
  useEffect(() => {
    if (!window.electronAPI) return;

    // Handle file opened from Electron
    const handleFileOpened = (event, { filePath, content, fileName }) => {
      const newFileId = `file_${Date.now()}`;
      const newFile = {
        id: newFileId,
        name: fileName, // Keep full file name with extension for local files
        content,
        originalContent: content,
        createdAt: new Date().getTime(),
        updatedAt: new Date().getTime(),
        filePath,
        isSaved: true,
        isLocalFile: true
      };

      dispatch({
        type: MARKDOWN_FILE_ACTIONS.received,
        item: newFile
      });
      dispatch({
        type: MARKDOWN_CURRENT_FILE,
        value: newFileId
      });
    };

    // Handle file saved from Electron
    const handleFileSaved = (event, { filePath, fileName }) => {
      if (currentFile) {
        const updatedFile = {
          ...currentFile,
          filePath,
          name: fileName, // Keep full file name with extension for local files
          isSaved: true,
          isLocalFile: true,
          originalContent: currentFile.content
        };

        dispatch({
          type: MARKDOWN_FILE_ACTIONS.updated,
          item: updatedFile
        });
      }
    };

    // Handle save request from menu
    const handleRequestSaveFile = () => {
      if (currentFile) {
        if (currentFile.filePath && currentFile.isSaved) {
          // File already saved, just save to the same path
          window.electronAPI.saveFile({
            filePath: currentFile.filePath,
            content: currentFile.content
          });
        } else {
          // File not saved yet, show save dialog
          window.electronAPI.saveFile({
            filePath: null,
            content: currentFile.content,
            defaultFileName: currentFile.name
          });
        }
      }
    };

    // Handle save as request from menu
    const handleRequestSaveAsFile = () => {
      if (currentFile) {
        window.electronAPI.saveFile({
          filePath: null,
          content: currentFile.content,
          defaultFileName: currentFile.name
        });
      }
    };

    // Handle save error
    const handleFileSaveError = (event, { error }) => {
      console.error('File save error:', error);
      // Optionally dispatch an error message to show to user
      dispatch({
        type: OPERATION_FAILED,
        message: `Failed to save file: ${error}`
      });
    };

    // Handle export requests from Electron menu
    const handleRequestExportMarkdown = () => {
      handleExportLocal();
    };

    const handleRequestExportPDF = () => {
      handleExportPDF();
    };

    const handleRequestExportImage = () => {
      handleExportImage();
    };

    const handleReopenClosedFile = (event, file) => {
      dispatch({
        type: MARKDOWN_FILE_ACTIONS.received,
        item: file
      });
      dispatch({
        type: MARKDOWN_CURRENT_FILE,
        value: file.id
      });
    };
    const handleLoginSuccess = (event, user) => {
      dispatch({
        type: 'LOGIN_IN',
        item: user
      });
    };

    window.electronAPI.onFileOpened(handleFileOpened);
    window.electronAPI.onFileSaved(handleFileSaved);
    window.electronAPI.onRequestSaveFile(handleRequestSaveFile);
    window.electronAPI.onRequestSaveAsFile(handleRequestSaveAsFile);
    window.electronAPI.onFileSaveError(handleFileSaveError);
    window.electronAPI.onRequestExportMarkdown(handleRequestExportMarkdown);
    window.electronAPI.onRequestExportPDF(handleRequestExportPDF);
    window.electronAPI.onRequestExportImage(handleRequestExportImage);
    window.electronAPI.onReopenClosedFile(handleReopenClosedFile);
    window.electronAPI.onUpdateLoginState(handleLoginSuccess);

    return () => {
      window.electronAPI.removeAllListeners('file-opened');
      window.electronAPI.removeAllListeners('file-saved');
      window.electronAPI.removeAllListeners('request-save-file');
      window.electronAPI.removeAllListeners('request-save-as-file');
      window.electronAPI.removeAllListeners('file-save-error');
      window.electronAPI.removeAllListeners('request-export-markdown');
      window.electronAPI.removeAllListeners('request-export-pdf');
      window.electronAPI.removeAllListeners('request-export-image');
      window.electronAPI.removeAllListeners('reopen-closed-file');
      window.electronAPI.removeAllListeners('update-login-state');
    };
  }, [currentFile, dispatch, handleExportLocal, handleExportPDF, handleExportImage]);

  const getScrollPercentage = (e) => {
    const element = e.target;
    const scrollPercentage = element.scrollTop / (element.scrollHeight - element.clientHeight);
    return scrollPercentage;
  };

  // Handle editor scroll
  const handleEditorScroll = useCallback((event) => {
    if (scrollSync && focusOn === 'editor' && previewRef?.current) {
      const scrollPercentage = getScrollPercentage(event);
      const maxScroll = previewRef.current.scrollHeight - previewRef.current.clientHeight;
      previewRef.current.scrollTop = maxScroll * scrollPercentage;
    }
  }, [scrollSync, focusOn]);

  // Handle preview scroll
  const handlePreviewScroll = useCallback((event) => {
    const editorScoller = document.querySelector('.cm-scroller');
    if (scrollSync && focusOn === 'preview' && editorScoller) {
      const scrollPercentage = getScrollPercentage(event);
      const maxScroll = editorScoller.scrollHeight - editorScoller.clientHeight;
      editorScoller.scrollTop = maxScroll * scrollPercentage;
    }
  }, [scrollSync, focusOn]);

  // Handle divider drag
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const newLeftWidth = (e.clientX / window.innerWidth) * 100;
      if (newLeftWidth >= 20 && newLeftWidth <= 80) {
        setLeftWidth(newLeftWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...style
      }}

      onKeyDown={(event) => {
        if ((OSName === 'win' && event.ctrlKey || OSName === 'mac' && event.metaKey) && (event.key === 's' || event.key === 'S')) {
          event.preventDefault();
          event.stopPropagation();

          // Handle save
          if (window.electronAPI && currentFile) {
            if (currentFile.filePath && currentFile.isSaved) {
              // File already saved, just save to the same path
              window.electronAPI.saveFile({
                filePath: currentFile.filePath,
                content: currentFile.content
              });
            } else {
              // File not saved yet, show save dialog
              window.electronAPI.saveFile({
                filePath: null,
                content: currentFile.content,
                defaultFileName: currentFile.name
              });
            }
          }

          return;
        }
      }}
    >
      {/* Header Menu bar */}
      <div
        style={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: '36px !important',
            gap: 1,
            width: '100%',
            maxWidth: '100%',
            padding: '0px 10px !important',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'space-between',
            overflow: 'hidden', // 防止超出屏幕
          }}
        >
          {/* 左侧区域：Logo */}
          <Box sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexShrink: 0 // Logo 区域不收缩
          }}>
            {/* Logo - 不收缩 */}
            <Tooltip title="Open FunBlocks AI homepage">
              <Box
                component="img"
                src={logo}
                alt="App Logo"
                sx={{
                  height: 28,
                  width: 28,
                  objectFit: 'contain',
                  marginRight: 1,
                  cursor: 'pointer',
                  flexShrink: 0
                }}
                onClick={() => {
                  const url = `https://${getMainDomain()}/`;
                  if (window.electronAPI) {
                    window.electronAPI.openExternal(url);
                  } else {
                    window.open(url, '_blank');
                  }
                }}
              />
            </Tooltip>
          </Box>

          {/* 中间区域：文件管理 + 导出 - 关键：整体作为一个可伸缩单元 */}
          <Box sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            minWidth: 0,
            flex: '1 1 auto', // 可以增长和收缩
            overflow: 'hidden'
          }}>
            {/* 文件管理区域 - 跟随内容宽度 */}
            {(
              <Box sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flex: '0 1 auto', // 不主动增长，但可以收缩
                minWidth: 0,
                overflow: 'hidden'
              }}>
                {/* File Tabs */}
                {
                  !!currentFileId && !embedded &&
                  <Tabs
                    value={currentFileId}
                    onChange={(e, newValue) => handleFileChange(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      minHeight: '30px',
                      flex: '0 1 auto', // 根据内容宽度
                      minWidth: 0,
                      borderBottom: '1px solid skyblue',
                      paddingLeft: '8px',
                      paddingRight: '8px',
                      paddingTop: '2px',
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#1976d2'
                      },
                      '& .MuiTabs-scrollButtons': {
                        '&.Mui-disabled': { opacity: 0.3 }
                      },
                      '& .MuiTab-root': {
                        minHeight: '36px',
                        padding: '0px 10px',
                        paddingRight: '6px',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        minWidth: 'auto',
                        maxWidth: '200px',
                        color: '#666', // Default color for unselected tabs

                        // backgroundColor: '#f0f0f0', // Default background for unselected tabs
                        // marginRight: '4px', // Space between tabs
                        borderRadius: '8px 8px 0 0', // Rounded top corners
                        '&.Mui-selected': {
                          color: '#1976d2',
                          fontWeight: 500,
                          backgroundColor: 'azure', // White background for selected tab
                          border: '0.5px solid skyblue', // Border for selected tab
                          borderBottom: '2px solid #1976d2', // Blue bottom border for selected tab
                        },
                        '&:hover': {
                          backgroundColor: '#f0f0f0', // Darker background on hover
                        }
                      }
                    }}
                  >
                    {memoizedMarkdownFiles.map(file => (
                      <Tab
                        key={file.id}
                        value={file.id}
                        label={
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <span style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '150px'
                            }}>
                              {file.name}
                            </span>
                            <IconButton
                              size="small"
                              onClick={(e) => handleCloseFile(e, file)}
                              onMouseEnter={() => setHoveredTabId(file.id)}
                              onMouseLeave={() => setHoveredTabId(null)}
                              sx={{
                                padding: '2px',
                                marginLeft: '2px',
                                width: '18px', // Make it circular
                                height: '18px', // Make it circular
                                borderRadius: '50%', // Make it circular
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.1)'
                                }
                              }}
                            >
                              {hoveredTabId === file.id ? (
                                <CloseIcon sx={{ fontSize: '11px' }} />
                              ) : (
                                <Box sx={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: '#999'
                                }} />
                              )}
                            </IconButton>
                          </Box>
                        }
                      />
                    ))}
                  </Tabs>
                }

                {/* File List Dropdown */}
                {!embedded && memoizedMarkdownFiles.length > 3 && (
                  <>
                    <Tooltip title="All Files">
                      <IconButton
                        size="small"
                        onClick={(e) => setFileMenuAnchor(e.currentTarget)}
                        sx={{ color: '#666', flexShrink: 0 }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={fileMenuAnchor}
                      open={fileMenuOpen}
                      onClose={() => setFileMenuAnchor(null)}
                    >
                      {memoizedMarkdownFiles.map((file) => (
                        <MenuItem
                          key={file.id}
                          selected={file.id === currentFileId}
                          onClick={() => {
                            handleFileChange(file.id);
                            setFileMenuAnchor(null);
                          }}
                        >
                          {file.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}


                {/* New File Button - 紧贴 tabs */}
                {!embedded &&
                  <Tooltip title="New File">
                    <IconButton
                      size="small"
                      onClick={handleNewFile}
                      sx={{ color: '#666', flexShrink: 0 }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                }

                {/* Import Button - 紧贴新建按钮 */}
                {
                  !embedded &&
                  !window.electronAPI &&
                  <Tooltip title="Import">
                    <IconButton
                      size="small"
                      onClick={handleImport}
                      sx={{ color: '#666', flexShrink: 0 }}
                    >
                      <UploadFileIcon />
                    </IconButton>
                  </Tooltip>
                }

                {!window.electronAPI && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                    {/* Export Menu Button */}
                    <Tooltip title="Export & Publish">
                      <IconButton
                        size="small"
                        onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                        sx={{ color: '#666' }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>

                    {/* Export Menu */}
                    <Menu
                      anchorEl={exportMenuAnchor}
                      open={exportMenuOpen}
                      onClose={() => setExportMenuAnchor(null)}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      <MenuItem onClick={handleExportLocal}>
                        <ListItemIcon>
                          <DownloadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Export as Markdown</ListItemText>
                      </MenuItem>

                      <MenuItem onClick={handleExportPDF}>
                        <ListItemIcon>
                          <PdfIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Export as PDF</ListItemText>
                      </MenuItem>

                      <MenuItem onClick={handleExportImage}>
                        <ListItemIcon>
                          <ImageIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Export as Image</ListItemText>
                      </MenuItem>

                      <Divider />

                      <MenuItem onClick={handleSaveToFunBlocks}>
                        <ListItemIcon>
                          <CloudUploadIcon fontSize="small" style={{ color: '#4285F4' }} />
                        </ListItemIcon>
                        <ListItemText>Save to FunBlocks Workspace</ListItemText>
                      </MenuItem>

                      <MenuItem onClick={() => handleConvertSlides()}>
                        <ListItemIcon>
                          <Slideshow size={19} fontSize="small" style={{ color: '#4285F4' }} />
                        </ListItemIcon>
                        <ListItemText>Generate Slides for Presentation</ListItemText>
                      </MenuItem>

                      <MenuItem onClick={() => handleConvertFlow()}>
                        <ListItemIcon>
                          <MindMap size={19} fontSize="small" style={{ color: '#4285F4' }} />
                        </ListItemIcon>
                        <ListItemText>Spark Ideas with AI on an Open Canvas</ListItemText>
                      </MenuItem>
                    </Menu>

                    {/* Loading Indicator */}
                    {loading && (
                      <CircularProgress
                        size={20}
                        sx={{ color: '#666' }}
                      />
                    )}
                  </Box>
                )}

              </Box>
            )}
          </Box>

          {/* 右侧区域：预览设置 + 用户信息 - 不收缩 */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0 // 右侧区域不收缩
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPreviewEnabled}
                  onChange={(e) => setIsPreviewEnabled(e.target.checked)}
                  size="small"
                  sx={{ padding: '4px' }}
                />
              }
              label="Preview"
              sx={{
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  color: '#666',
                  whiteSpace: 'nowrap' // 防止换行
                }
              }}
            />

            {isPreviewEnabled && (
              <FormControlLabel
                control={
                  <Switch
                    checked={scrollSync}
                    onChange={(e) => setScrollSync(e.target.checked)}
                    size="small"
                  />
                }
                label="Sync Scroll"
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    color: '#666',
                    whiteSpace: 'nowrap' // 防止换行
                  }
                }}
              />
            )}

            {!embedded && (
              <>
                <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center' }} />

                {!loginUser?._id && (
                  <Tooltip title={intl.formatMessage({ id: 'settings' })} placement="bottom">
                    <IconButton
                      size="small"
                      onClick={() => {
                        dispatch({ type: SETTINGS_DIALOG, value: { visible: true, app: 'markdown' } });
                      }}
                      sx={{ color: 'dodgerblue' }}
                    >
                      <Settings size={18} />
                    </IconButton>
                  </Tooltip>
                )}

                {loginUser?._id ? (
                  <Button
                    size="small"
                    startIcon={<Avatar sx={{ width: 28, height: 28, backgroundColor: 'dodgerblue' }} src={loginUser.avatarUrl}>{loginUser.nickname.charAt(0)}</Avatar>}
                    sx={{ textTransform: 'none', color: '#333', fontWeight: 500, whiteSpace: 'nowrap' }}
                    onClick={() => {
                      dispatch({ type: SETTINGS_DIALOG, value: { visible: true, app: 'markdown' } });
                    }}
                  >
                    {loginUser.nickname}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      const url = '/#/login?source=markdown';
                      if (isElectronApp()) {
                        window.electronAPI.openLoginWindow(url);
                      } else {
                        window.open(url, '_blank');
                      }
                    }}
                    sx={{ height: '32px', whiteSpace: 'nowrap' }}
                  >
                    {intl.formatMessage({ id: 'login' })}
                  </Button>
                )}

                {loginUser?._id && (
                  <Tooltip title={intl.formatMessage({ id: 'logout' })} placement="bottom">
                    <IconButton
                      size="small"
                      onClick={() => {
                        dispatch(logout_of_server({}));
                      }}
                      sx={{ color: '#666' }}
                    >
                      <LogoutBoxR size={18} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}

            {embedded && (
              <Tooltip
                title={intl.formatMessage({ id: 'expand' })}
                placement='bottom'
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    window.open(`https://app.${getMainDomain()}/#/markdown`, '_blank')
                  }}
                  sx={{ color: '#666' }}
                >
                  <ArrowMaximize size={18} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </div>

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".md,.markdown,.txt,.text"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      {/* Editor and Preview */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          overflow: 'hidden',
          userSelect: isDragging ? 'none' : 'auto'
        }}
      >
        {/* Editor */}
        <div
          style={{ width: !isPreviewEnabled ? '100%' : `${leftWidth}%`, display: 'flex', flexDirection: 'column' }}
          onMouseEnter={() => setFocusOn('editor')}
        >
          <CmEditor
            key={currentFileId}
            ref={editorRef}
            app={'markdown'}
            onChange={handleContentChange}
            onScroll={handleEditorScroll}
            initialDoc={{ hid: currentFileId, markdown: markdownFilesById[currentFileId]?.content || '' }}
          />
        </div>

        {/* Divider */}
        {
          isPreviewEnabled &&
          <div
            onMouseDown={() => setIsDragging(true)}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{
              width: '3px',
              cursor: 'col-resize',
              backgroundColor: isDragging ? 'dodgerblue' : '#e0e0e0',
              transition: 'background-color 0.2s',
              position: 'relative',
              flexShrink: 0
            }}
          >
            {/* Drag Handle */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '14px',
                height: '40px',
                backgroundColor: (isDragging || isHovering) ? 'dodgerblue' : '#bebebe',
                borderRadius: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                transition: 'background-color 0.2s',
                cursor: 'col-resize'
              }}
            >
              <DragIcon sx={{ color: '#fff', fontSize: 16 }} />
            </div>
          </div>
        }

        {
          isPreviewEnabled &&
          <div
            style={{
              width: `${100 - leftWidth}%`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={() => setFocusOn('preview')}
          >
            <MarkdownPreview
              content={content}
              onScroll={handlePreviewScroll}
              scrollSync={scrollSync}
              previewRef={previewRef}
            />
          </div>
        }

      </div>
    </div>
  );
};

export default MdEditorFull;